/**
 * ReplyCheckoutModal — In-page payment drawer for Reply Rescue.
 *
 * Replaces the redirect-to-Stripe-hosted-page flow on the LandingReply CTAs.
 * Opens as a slide-up sheet on mobile / centered modal on desktop. Inside it
 * mounts the shared <InlinePayment> with the `rose` Reply Rescue theme,
 * English copy, and USD pricing.
 *
 * The previous CTA flow:
 *    button click → POST /reply/checkout → window.location = Stripe URL
 *
 * The new flow:
 *    button click → open this modal → POST /reply/payment-intent (debounced)
 *    → render PaymentElement → user pays → Stripe redirects to
 *    /reply/session/{public_token}?payment=success (existing success path).
 */

import { useEffect, useState } from "react";
import { X, Check } from "lucide-react";
import InlinePayment from "./InlinePayment";
import InAppBrowserBanner from "./InAppBrowserBanner";
import { api } from "../lib/api";

// Pretty-print the package name (matches LandingReply's CTA wording).
const PACKAGE_DISPLAY = {
  reply_rescue_5credits: { title: "Rescue Session", credits: "5 replies", price: 9.99 },
  reply_rescue_15credits: { title: "Deep Rescue", credits: "15 replies", price: 19.99 },
  reply_rescue_24h_pass: { title: "24h Reply Pass", credits: "Unlimited 24h (fair-use 40)", price: 14.99 },
};

const ReplyCheckoutModal = ({ open, onClose, packageKey, visitorId, onAddToCart }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [publicToken, setPublicToken] = useState(null);
  const [piLoading, setPiLoading] = useState(false);
  const [err, setErr] = useState(null);
  // Visitor's country (from IP geo) — passed as Stripe defaultCountry so
  // the billing-country dropdown matches the visitor's actual location.
  // Reply Rescue is global so we fall back to US if geo fails.
  const [country, setCountry] = useState("US");
  useEffect(() => {
    let cancelled = false;
    api.get("/geo")
      .then((r) => { if (!cancelled) {
        const c = (r.data?.country || "").toUpperCase();
        if (c && c.length === 2) setCountry(c);
      } })
      .catch(() => { /* keep US default */ });
    return () => { cancelled = true; };
  }, []);

  const pkg = PACKAGE_DISPLAY[packageKey] || PACKAGE_DISPLAY.reply_rescue_5credits;

  // Mint a fresh PaymentIntent whenever the modal opens with a new package.
  // We reset the intent on close so a re-open always gets a clean slate (avoids
  // showing a stale "card declined" error from a previous attempt).
  useEffect(() => {
    if (!open || !packageKey) {
      setClientSecret(null);
      setPublicToken(null);
      setErr(null);
      return;
    }
    let cancelled = false;
    setPiLoading(true);
    setErr(null);
    (async () => {
      try {
        const { data } = await api.post("/reply/payment-intent", {
          package: packageKey,
          visitor_id: visitorId || "",
        });
        if (cancelled) return;
        setClientSecret(data.client_secret);
        setPublicToken(data.public_token);
      } catch (e) {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      } finally {
        if (!cancelled) setPiLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, packageKey, visitorId]);

  // Lock the body scroll while open (improves mobile UX).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      data-testid="reply-checkout-modal"
      role="dialog"
      aria-modal="true"
    >
      {/* IAB Blocker — when the visitor opens this checkout modal inside an
          in-app browser (TikTok/IG/FB/Snapchat), the banner takes over the
          full screen. They can read the landing page freely (no blocker
          there) but the moment they reach payment we force them to a real
          browser since Stripe Payment Element won't work in IAB. */}
      <InAppBrowserBanner />
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0A0410]/85 backdrop-blur-sm"
        onClick={onClose}
        data-testid="reply-checkout-backdrop"
      />

      {/* Sheet / modal */}
      <div
        className="relative w-full sm:max-w-md bg-[#1A0E22] border-t sm:border border-[#D9707F]/30 sm:rounded-2xl rounded-t-2xl shadow-[0_30px_80px_-25px_rgba(217,112,127,0.45)] max-h-[92vh] overflow-y-auto"
        dir="ltr"
        data-testid="reply-checkout-sheet"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-[#F6EFE7] flex items-center justify-center transition-colors"
          data-testid="reply-checkout-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 sm:p-7">
          {/* Header */}
          <div className="text-[10.5px] tracking-[0.26em] text-[#E8A18F] font-bold mb-2">REPLY RESCUE</div>
          <h2 className="text-[22px] font-extrabold text-[#F6EFE7] leading-tight">
            {pkg.title}
          </h2>
          <p className="text-[12.5px] text-[#A795B8] mt-1.5">
            Private chat session — paste your conversation, get AI-powered replies instantly.
          </p>

          {/* Order summary */}
          <div className="mt-5 mb-5 rounded-xl bg-[#15081C] border border-[#D9707F]/20 p-4" data-testid="reply-checkout-summary">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[12px] text-[#A795B8]">You'll get</div>
                <div className="text-[14px] font-bold text-[#F6EFE7] mt-0.5 flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-[#E8A18F]" /> {pkg.credits}
                </div>
                <div className="text-[11.5px] text-[#A795B8] mt-1">No subscription · No account needed</div>
              </div>
              <div className="text-right">
                <div className="text-[22px] font-extrabold text-[#F6EFE7] tabular-nums">${pkg.price.toFixed(2)}</div>
                <div className="text-[11px] text-[#A795B8]">USD · one-time</div>
              </div>
            </div>
          </div>

          {/* Error */}
          {err && (
            <div
              className="rounded-xl p-3 text-[13px] font-semibold mb-4"
              style={{ background: "rgba(229,72,77,0.10)", border: "1px solid rgba(229,72,77,0.35)", color: "#FFB4B7" }}
              data-testid="reply-checkout-err"
            >
              {err}
            </div>
          )}

          {/* Inline payment */}
          <InlinePayment
            clientSecret={clientSecret}
            orderToken={publicToken}
            amount={pkg.price.toFixed(2)}
            currency="USD"
            currencyLabel="USD"
            returnPath={publicToken ? `/reply/session/${publicToken}?payment=success&pkg=${packageKey}` : "/reply"}
            theme="rose"
            testid="reply-inline-payment"
            buttonLabel={`Pay $${pkg.price.toFixed(2)} — ${pkg.title}`}
            direction="ltr"
            trustText="Secure payment via Stripe · 7-day refund · Private access link"
            processingText="Processing…"
            loadingText="Preparing secure checkout…"
            unavailableText="Payment system is temporarily unavailable — please try again shortly."
            beforeConfirm={onAddToCart}
            defaultCountry={country}
          />

          {piLoading && !clientSecret && (
            <div className="text-center text-[11.5px] text-[#A795B8] mt-2" data-testid="reply-checkout-loading">
              Preparing your secure checkout…
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReplyCheckoutModal;
